import { useMutation, useQuery } from '@apollo/client';
import { EWorldMapInitialType, WorldMapInput } from '__generated__/globalTypes';
import { InitialBoundsData } from 'components/map/location/defaultMapLocation';
import { constructInitPositonMarker } from 'components/minute/sidebar/MapSidebar/MapSettingsSection/constructInitPositonMarker';
import { InitialBounds } from 'components/minute/sidebar/MapSidebar/MapSettingsSection/InitialBounds';
import { InitialPosition } from 'components/minute/sidebar/MapSidebar/MapSettingsSection/InitialPosition';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { NumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/NumberValue';
import { OptionRowValue } from 'elements/minute/sidebar/sidebarComponents/atomic/OptionRowValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import {
    UpdateWorldMap,
    UpdateWorldMapVariables
} from 'graphql/mutation/__generated__/UpdateWorldMap';
import { UPDATE_WORLD_MAP_MUTATION } from 'graphql/mutation/worldMutation';
import { WorldMap_worldMap_initial_bounds } from 'graphql/query/__generated__/WorldMap';
import {
    WorldMapInitialBounds,
    WorldMapInitialBoundsVariables
} from 'graphql/query/__generated__/WorldMapInitialBounds';
import { WORLD_MAP_INITIAL_BOUNDS_QUERY } from 'graphql/query/worldQuery';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { EFonts } from 'libs/styles/styling';
import { omitDeep } from 'libs/utils/object/objectOperations';
import { pick } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';
import { PartialDeep } from 'type-fest';
import { useUpdateEffect } from 'usehooks-ts';

interface Props {
    className?: string;
}

export const MapInitialPositionSubsection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const { getTokenForMapId } = useWorldMapLocalStorage();

    const [visibleRectangle, setVisibleRectangle] = useState<
        null | 'auto' | 'desktop' | 'mobile'
    >();

    const [useBounds, setUseBounds] = useState(ctxEdit.map.initial?.useBounds);
    const [isAutomatic, setIsAutomatic] = useState(
        ctxEdit.map.initialType === EWorldMapInitialType.AUTOMATIC
    );
    const [pcPosition, setPcPosition] = useState(pick(ctxEdit.map.initial, ['lat', 'lng', 'zoom']));
    const [mobilePosition, setMobilePosition] = useState(
        pick(ctxEdit.map.initialMobile, ['lat', 'lng', 'zoom'])
    );
    const [pcBounds, setPcBounds] = useState<InitialBoundsData>(
        omitDeep(ctxEdit.map.initial.bounds, ['__typename'])
    );
    const [mobileBounds, setMobileBounds] = useState<InitialBoundsData>(
        omitDeep(ctxEdit.map.initialMobile.bounds, ['__typename'])
    );
    const [boundsPadding, setBoundsPadding] = useState(
        ctxEdit.map.initial?.autoBounds.padding ?? 10
    );
    const [sendMutation] = useMutation<UpdateWorldMap, UpdateWorldMapVariables>(
        UPDATE_WORLD_MAP_MUTATION
    );

    const updateWorldMap = (data: PartialDeep<WorldMapInput>) => {
        sendMutation({
            variables: {
                worldMapId: ctxEdit.map?.worldMapId,
                token: getTokenForMapId(ctxEdit.map?.worldMapId),
                map: data
            }
        });
    };

    function update() {
        const initialType = isAutomatic
            ? EWorldMapInitialType.AUTOMATIC
            : EWorldMapInitialType.CUSTOM;

        ctxEdit.setMap({
            ...ctxEdit.map,
            initialType,
            initial: {
                ...ctxEdit.map.initial,
                ...pcPosition,
                bounds: boundsDataToGraphQL(pcBounds),
                autoBounds: {
                    ...boundsDataToGraphQL(ctxEdit.map.initial.autoBounds),
                    padding: boundsPadding
                }
            },
            initialMobile: {
                ...ctxEdit.map.initialMobile,
                ...mobilePosition,
                bounds: boundsDataToGraphQL(mobileBounds)
            }
        });

        updateWorldMap({
            initialType,
            initial: {
                useBounds,
                ...pcPosition,
                bounds: omitDeep({ ...pcBounds }, ['__typename']),
                autoBounds: omitDeep(
                    { ...data.data.worldMap.initial.autoBounds, padding: boundsPadding },
                    ['__typename']
                )
            },
            initialMobile: {
                useBounds,
                ...mobilePosition,
                bounds: omitDeep({ ...mobileBounds }, ['__typename'])
            }
        });
    }

    useUpdateEffect(() => {
        update();
    }, [useBounds, isAutomatic, boundsPadding, pcPosition, mobilePosition, pcBounds, mobileBounds]);

    const boundsDataToGraphQL = (
        data: InitialBoundsData,
        padding?: number
    ): WorldMap_worldMap_initial_bounds => {
        return {
            topLeft: {
                lat: data.topLeft?.lat ?? 0,
                lng: data.topLeft?.lng ?? 0,
                __typename: 'Coordinates'
            },
            bottomRight: {
                lat: data.bottomRight?.lat ?? 0,
                lng: data.bottomRight?.lng ?? 0,
                __typename: 'Coordinates'
            },
            padding,
            __typename: 'WorldMapInitialBounds'
        };
    };

    const data = useQuery<WorldMapInitialBounds, WorldMapInitialBoundsVariables>(
        WORLD_MAP_INITIAL_BOUNDS_QUERY,
        {
            variables: {
                worldMapId: ctxEdit.map?.worldMapId,
                token: getTokenForMapId(ctxEdit.map?.worldMapId)
            }
        }
    );

    const getBounds = (): InitialBoundsData => {
        if (visibleRectangle === 'auto') {
            return data.data?.worldMap.initial.autoBounds;
        }

        if (visibleRectangle === 'desktop') {
            return pcBounds;
        }
        if (visibleRectangle === 'mobile') {
            return mobileBounds;
        }
    };

    const bounds = getBounds();

    useEffect(() => {
        if (isAutomatic) {
            data.refetch();
        }
    }, [isAutomatic]);

    useEffect(() => {
        const marker = constructInitPositonMarker(bounds, ctxEdit.map.initialType);
        ctxEdit.setShowInitMapRectangle(marker);

        return () => {
            ctxEdit.setShowInitMapRectangle(null);
        };
    }, [bounds?.bottomRight, bounds?.topLeft, bounds?.padding, isAutomatic]);

    return (
        <S_MapInitialPositionSubsection className={props.className}>
            <SidebarSubsection
                title={Localization.INITIAL_POSITION}
                icon={{ iconName: 'location-dot' }}
            >
                <SidebarContent>
                    <OptionRowValue
                        items={[
                            {
                                text: 'Auto'
                            },
                            {
                                text: 'Bounds'
                            },
                            {
                                text: 'Position'
                            }
                        ]}
                        selectedIndex={(() => {
                            if (isAutomatic) return 0;
                            if (useBounds) return 1;
                            return 2;
                        })()}
                        onSelected={(index) => {
                            setUseBounds(index === 0 || index === 1);
                            setIsAutomatic(index === 0);
                            setVisibleRectangle(null);
                        }}
                    />
                </SidebarContent>
                <SidebarContent>
                    {!useBounds && (
                        <>
                            <S_SubHeading>
                                <S_SubHeadingText>PC</S_SubHeadingText>
                            </S_SubHeading>
                            <InitialPosition
                                initialPosition={ctxEdit.map.initial}
                                onChange={(data) => {
                                    setPcPosition((curr) => ({ ...curr, ...data }));
                                }}
                            />

                            <S_SubHeading>
                                <S_SubHeadingText>Mobile</S_SubHeadingText>
                            </S_SubHeading>
                            <InitialPosition
                                initialPosition={ctxEdit.map.initialMobile}
                                onChange={(data) => {
                                    setMobilePosition((curr) => ({ ...curr, ...data }));
                                }}
                            />
                        </>
                    )}
                    {useBounds && (
                        <>
                            {isAutomatic && (
                                <>
                                    <SidebarSectionRow title="Camera padding">
                                        <NumberValue
                                            value={boundsPadding}
                                            onChange={(e) => {
                                                setBoundsPadding(e);
                                            }}
                                            width="75px"
                                        />
                                    </SidebarSectionRow>
                                    <ActionButton
                                        icon={{ iconName: 'eye' }}
                                        highlighted={visibleRectangle === 'auto'}
                                        onClick={() => {
                                            setVisibleRectangle(
                                                visibleRectangle === 'auto' ? null : 'auto'
                                            );
                                        }}
                                    />
                                </>
                            )}
                            {!isAutomatic && (
                                <>
                                    <S_SubHeading>
                                        <S_SubHeadingText>PC</S_SubHeadingText>
                                        <ActionButton
                                            icon={{ iconName: 'eye' }}
                                            highlighted={visibleRectangle === 'desktop'}
                                            onClick={() => {
                                                setVisibleRectangle(
                                                    visibleRectangle === 'desktop'
                                                        ? null
                                                        : 'desktop'
                                                );
                                            }}
                                        />
                                    </S_SubHeading>
                                    <InitialBounds
                                        initialBounds={ctxEdit.map.initial.bounds}
                                        onChange={(data) => {
                                            setPcBounds(data);
                                        }}
                                    />
                                    <S_SubHeading>
                                        <S_SubHeadingText>Mobile</S_SubHeadingText>
                                        <ActionButton
                                            icon={{ iconName: 'eye' }}
                                            highlighted={visibleRectangle === 'mobile'}
                                            onClick={() => {
                                                setVisibleRectangle(
                                                    visibleRectangle === 'mobile' ? null : 'mobile'
                                                );
                                            }}
                                        />
                                    </S_SubHeading>
                                    <InitialBounds
                                        initialBounds={ctxEdit.map.initialMobile.bounds}
                                        onChange={(data) => {
                                            setMobileBounds(data);
                                        }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </SidebarContent>
            </SidebarSubsection>
        </S_MapInitialPositionSubsection>
    );
};

const S_MapInitialPositionSubsection = styled.div``;

const S_SubHeading = styled.div`
    display: flex;
    margin-bottom: 6px;
    align-items: center;
`;

const S_SubHeadingText = styled.div`
    display: flex;
    font-weight: ${EFonts.Medium};
    font-size: 14px;
    margin-right: 10px;
`;
